import { Text, VStack } from '@chakra-ui/react'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUserState } from '~/atoms/UserState'
import { SelectItem } from '~/components/shared/Dropdown/DropdownSelect'
import { MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import { ScrollingInputDropdown } from '~/components/shared/Dropdown/ScrollingInputDropdown'
import { ContactSearchListItem } from '~/components/specialized/ContactSearchInput/ContactSearchListItem'
import { useAnalytics } from '~/hooks/useAnalytics'
import { useDebounce } from '~/hooks/useDebounce'
import { useGetContacts, useGetContactSources } from '~/queries/contactsQueries'
import { AnalyticsNamespace, ContactsAnalyticsEvents } from '~/types/Analytics'
import { getPaginatedResultParts } from '~/utils/apiQueryUtils'
import { hasValue, tryGetValueOrDefault } from '~/utils/common'
import { formatPhoneNumber } from '~/utils/formatter'

import { cleanDialstring } from '@mangovoice/core'
import {
  Contact,
  ContactPhoneNumber,
  ContactSource,
  ContactSourceType,
} from '@mangovoice/types'

const SEARCH_DEBOUNCE_RATE_MS = 300
const SEARCH_CONTACTS_PAGE_SIZE = 25
const SEARCH_CACHE_TIME_MS = 60 * 1000 /* 1 minute */
const CONTACT_SOURCE_CACHE_TIME_MS = 10 * 60 * 1000 /* 10 minutes */

interface ContactSearchInputProps {
  contactSourceTypeFilter?: ContactSourceType[]
  isInvalid?: boolean
  value?: string
  onValueChanged?: (value: string) => void
}

type ContactLabelValue = {
  phoneNumber: string
  contactName: string
  isExtension: boolean
  hasNumber: boolean
}
interface ContactLabelData {
  value: ContactLabelValue
  node: React.ReactNode
  disabled: boolean
}
const getContactLabelData = (
  contacts?: Contact[],
  contactSources?: ContactSource[],
  sourceTypeFilter?: ContactSourceType[],
  noNumberLabel?: string,
): ContactLabelData[] => {
  if (!contacts) {
    return []
  }

  // Wait for sources before filtering; without them we'd leak filtered
  // contacts (e.g. extensions) into the list.
  if (sourceTypeFilter && sourceTypeFilter.length > 0 && !contactSources) {
    return []
  }

  const extensionSourceId = contactSources?.find(
    (cs) => cs.source === ContactSourceType.Extensions,
  )?.id

  const labels = contacts.map((contact) => {
    if (sourceTypeFilter && sourceTypeFilter.length > 0) {
      const isSourceFiltered = contactSources?.some(
        (cs) =>
          cs.id === contact.contact_source_id &&
          sourceTypeFilter.includes(cs.source),
      )
      if (isSourceFiltered) {
        return []
      }
    }

    const isExtension = contact.contact_source_id === extensionSourceId
    // Grove/PMS contacts may return their number on `primary_phone_number` while
    // `phone_numbers` is empty or holds placeholder entries with no `number`;
    // internal contacts populate `phone_numbers`. Merge both, primary first (to
    // match the Contacts list), and keep whichever entries actually carry a
    // number so a valid number on either field shows.
    const combined: ContactPhoneNumber[] = [
      ...(contact.primary_phone_number ? [contact.primary_phone_number] : []),
      ...(contact.phone_numbers ?? []),
    ]
    const withNumbers = combined.filter((n) => hasValue(n.number))
    const seen = new Set<string>()
    const uniqueWithNumbers = withNumbers.filter((n) => {
      const key = n.number as string
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })

    const buildLabel = (
      phoneNumber: ContactPhoneNumber,
      hasNumber: boolean,
    ): ContactLabelData => ({
      value: {
        phoneNumber: phoneNumber.number ?? '',
        contactName: `${contact.given_name} ${contact.family_name}`,
        isExtension: Boolean(isExtension),
        hasNumber,
      },
      node: (
        <MenuListItem>
          <ContactSearchListItem
            contact={contact}
            contactPhoneNumber={phoneNumber}
            isExtension={isExtension}
            noNumberLabel={noNumberLabel}
          />
        </MenuListItem>
      ),
      disabled: !hasNumber,
    })

    if (uniqueWithNumbers.length > 0) {
      return uniqueWithNumbers.map((phoneNumber) =>
        buildLabel(phoneNumber, true),
      )
    }

    // No usable number: still surface the contact by name, but disabled so it
    // can't be picked as a fax/text destination.
    return [buildLabel({} as ContactPhoneNumber, false)]
  })

  return labels.flat()
}

export const ContactSearchInput = ({
  contactSourceTypeFilter,
  isInvalid,
  value,
  onValueChanged,
}: ContactSearchInputProps) => {
  const [isFocused, setIsInputFocused] = useState<boolean>(false)
  const [canSignalInvalid, setCanSignalInvalid] = useState<boolean>(false)
  const [contacts, setContacts] = useState<Contact[]>([])
  const [inputValue, setInputValue] = useState('')
  const [unformattedInputValue, setUnformattedInputValue] = useState<string>('')
  const [searchValue, setSearchValue] = useState({ term: '' })
  const [pageData, setPageData] =
    useState<ReturnType<typeof getPaginatedResultParts>>()
  const { pbxId } = useUserState()
  const { t } = useTranslation()

  if (value && value !== inputValue) {
    setInputValue(value)
  }

  const analytics = useAnalytics(AnalyticsNamespace.CONTACTS)

  const { data: contactSources } = useGetContactSources({
    pbxId: pbxId ?? 0,
    options: {
      enabled: true,
      staleTime: CONTACT_SOURCE_CACHE_TIME_MS,
      retry: 3,
    },
  })

  const {
    refetch: getContactsPage,
    isFetching: isContactsListFetching,
    tryCancelQuery: cancelContactsSearch,
  } = useGetContacts({
    pbxId: pbxId ?? 0,
    options: {
      staleTime: SEARCH_CACHE_TIME_MS,
    },
    onQuerySuccess(data) {
      const parts = getPaginatedResultParts(data)
      setPageData(parts)
      if (parts?.results) {
        setContacts((prev) => [...prev, ...parts.results])
      }
    },
  })

  // Derive labels from raw contacts so filtering reflects the latest sources.
  // Building labels in onQuerySuccess races the sources query and can leak
  // filtered contacts when contacts resolve first.
  const contactLabelData = useMemo(
    () =>
      getContactLabelData(
        contacts,
        contactSources?.results,
        contactSourceTypeFilter,
        t('contactSearchInput.noNumber'),
      ),
    [contacts, contactSources, contactSourceTypeFilter, t],
  )

  const handleFetchNextPage = () => {
    //don't fetch if we are already fetching or there are no more pages
    if (isContactsListFetching || !pageData?.hasNextPage) {
      return
    }

    getContactsPage({
      offset: pageData?.nextPageOffset ?? 0,
      limit: SEARCH_CONTACTS_PAGE_SIZE,
      ordering: ['given_name', 'family_name'],
      search: searchValue.term,
    })
  }

  const resetSearchState = () => {
    setContacts([])
    setPageData(undefined)
  }

  const [, cancelDebounce] = useDebounce(
    () => {
      const trimmed = unformattedInputValue.trim()
      setSearchValue({ term: trimmed })
    },
    SEARCH_DEBOUNCE_RATE_MS,
    [unformattedInputValue],
  )

  //on mount, don't update search value
  useEffect(() => {
    cancelDebounce()

    return () => {
      cancelDebounce()
    }
  }, [])

  //on search value change, fetch contacts
  useEffect(() => {
    getContactsPage({
      offset: 0,
      limit: SEARCH_CONTACTS_PAGE_SIZE,
      search: searchValue.term,
    })
  }, [searchValue])

  const handleFocus = (focused: boolean) => {
    setIsInputFocused(focused)

    if (!focused) {
      setCanSignalInvalid(true)
      return
    }

    // The contact list lives in local state cleared on every value change, so
    // re-focusing an empty input wouldn't reopen the menu. Re-fetch on refocus
    // when empty, but skip mid-edit since the debounced search covers that.
    // Guard on the raw `contacts` (not the filtered `contactLabelData`): when a
    // search returned only filtered-out results (e.g. all extensions), the
    // labels are empty but re-fetching would just append the same page again.
    const isMidEdit = unformattedInputValue.trim() !== searchValue.term
    if (!isContactsListFetching && !isMidEdit && contacts.length === 0) {
      getContactsPage({
        offset: 0,
        limit: SEARCH_CONTACTS_PAGE_SIZE,
        search: searchValue.term,
      })
    }
  }

  const handleValueChanged = (value: string, unformattedOverride?: string) => {
    cancelContactsSearch()
    cancelDebounce()
    resetSearchState()

    //try and format the input - if it's a valid number, it will be formatted
    const formattedValue = tryGetValueOrDefault(formatPhoneNumber(value), value)

    setInputValue(formattedValue!)
    onValueChanged?.(formattedValue!)

    const unformatted =
      unformattedOverride ??
      (formattedValue
        ? tryGetValueOrDefault(cleanDialstring(formattedValue), value)
        : value)

    setUnformattedInputValue(unformatted ?? '')
  }

  const handleSelected = (value: ContactLabelValue) => {
    const labelData = value
    // No-number contacts are surfaced for discoverability but can't be a
    // destination; the menu also disables them, this is a belt-and-suspenders.
    if (!labelData.hasNumber) {
      return
    }
    const contactPhone = labelData.phoneNumber
    const cleanedPhone = cleanDialstring(contactPhone)

    const unformattedOverride = labelData.isExtension
      ? labelData.contactName
      : undefined

    handleValueChanged(cleanedPhone, unformattedOverride)
    if (labelData.isExtension) {
      analytics.track(
        ContactsAnalyticsEvents.ContactSearchInputExtensionClicked,
      )
    } else {
      analytics.track(
        ContactsAnalyticsEvents.ContactSearchInputPhoneNumberClicked,
      )
    }
  }

  const shouldBeOpen = isFocused && contactLabelData.length > 0
  // Only flag invalid on blur — while focused the user may be typing a name to
  // search, which isn't a phone number and shouldn't show "invalid phone number".
  const isToInvalid = canSignalInvalid && isInvalid && !isFocused
  return (
    <VStack gap="0.375rem" align="stretch" width="100%">
      <ScrollingInputDropdown
        items={contactLabelData.map((data) => {
          return {
            value: data.value,
            content: data.node,
            clickDisabled: data.disabled,
          } as SelectItem<ContactLabelValue>
        })}
        isOpen={shouldBeOpen}
        value={inputValue}
        onMenuClose={() => handleFocus(false)}
        onFocus={() => handleFocus(true)}
        onScrollEndReached={handleFetchNextPage}
        autoCloseOnBlur
        autoCloseOnSelected
        onSelected={handleSelected}
        onValueChanged={handleValueChanged}
        disabled={!contactSources}
        placeholder={t('contactSearchInput.placeholder')}
        inputAriaLabel={t('a11y.recipient.label')}
        listboxAriaLabel={t('a11y.recipient.listLabel')}
        getResultsAnnouncement={(count) =>
          t('a11y.recipient.results', { count })
        }
        isInvalid={isToInvalid}
        isLoading={isContactsListFetching}
      />
      {isToInvalid && isInvalid && (
        <Text color="red.solid">
          {t('contactSearchInput.invalidPhoneNumber')}
        </Text>
      )}
    </VStack>
  )
}
